import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Home as HomeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';

const coverageExamples = [
  {
    issue: 'Undisclosed Liens',
    description: 'Previous owner had unpaid taxes or contractor liens that weren\'t discovered during the title search.',
    coverage: 'Full protection up to policy amount',
    example: 'A $15,000 tax lien from 2019 surfaces after closing - title insurance covers the full amount.',
  },
  {
    issue: 'Forged Documents',
    description: 'Someone forged a previous owner\'s signature on deeds or other documents in the chain of title.',
    coverage: 'Legal defense and financial protection',
    example: 'A deed from 1998 had a forged signature - title insurance pays for legal defense and any losses.',
  },
  {
    issue: 'Survey Errors',
    description: 'Boundary disputes or encroachments not revealed in the original survey.',
    coverage: 'Protection against boundary disputes',
    example: 'Neighbor\'s garage encroaches 2 feet onto your property - title insurance handles the resolution.',
  },
  {
    issue: 'Unknown Heirs',
    description: 'Heirs of previous owners surface with claims to the property.',
    coverage: 'Full legal defense and protection',
    example: 'A long-lost heir claims ownership rights - title insurance provides legal defense and protection.',
  },
];

const titleProcess = [
  {
    step: 1,
    title: 'Title Search Ordered',
    description: 'We search public records for the property\'s complete ownership history, typically going back 30-60 years.',
    timeframe: '1-2 business days',
    details: 'Our experienced abstractors examine records at Middlesex County Clerk\'s office and other repositories.',
  },
  {
    step: 2,
    title: 'Title Examination',
    description: 'Our title professionals review the search results to identify any issues or potential problems.',
    timeframe: '1-2 business days',
    details: 'We look for liens, judgments, easements, restrictions, and other encumbrances that could affect ownership.',
  },
  {
    step: 3,
    title: 'Title Commitment Issued',
    description: 'We provide a commitment showing what the title policy will cover and any exceptions.',
    timeframe: 'Same day after examination',
    details: 'The commitment outlines the policy terms and lists any issues that need to be resolved before closing.',
  },
  {
    step: 4,
    title: 'Issue Resolution',
    description: 'We work to resolve any title issues discovered during the examination process.',
    timeframe: 'Varies by issue complexity',
    details: 'This might include paying off liens, obtaining releases, or working with attorneys to clear title defects.',
  },
  {
    step: 5,
    title: 'Closing & Policy Issuance',
    description: 'At closing, we issue the title insurance policy providing ongoing protection.',
    timeframe: 'At closing',
    details: 'The policy provides coverage from the closing date forward, protecting against covered title defects.',
  },
];

const TitleInsuranceGuide: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>How Title Insurance Works in Woodbridge, NJ | MAD Title Agency Guide</title>
        <meta
          name="description"
          content="Complete guide to title insurance for Middlesex County homebuyers. Learn how title insurance protects your investment in Woodbridge, Edison, and surrounding areas."
        />
        <meta
          name="keywords"
          content="title insurance Woodbridge NJ, Middlesex County title insurance, how title insurance works, Woodbridge homebuyers, title protection"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Title Insurance Guide
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Consumer Guide"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                How Title Insurance Works in Woodbridge, NJ
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Complete guide to title insurance for Middlesex County homebuyers
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Guide PDF
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Protect Your Investment
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Lifetime Coverage
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            What is Title Insurance?
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Title insurance is a form of indemnity insurance that protects you and your lender from 
                financial loss due to defects in a property's title. Unlike other types of insurance that 
                protect against future events, title insurance protects against issues from the past that 
                could affect your ownership rights.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                In New Jersey, and specifically in Middlesex County communities like Woodbridge, Edison, 
                and Perth Amboy, title insurance is essential due to the area's rich history and complex 
                property ownership patterns dating back centuries.
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Did you know?</strong> Some properties in Woodbridge have ownership records 
                  dating back to the 1600s. Title insurance protects against issues that might arise 
                  from this extensive history.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f0f9ff' }}>
                <HomeIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Your Home is Protected
                </Typography>
                <Typography variant="body2">
                  Title insurance provides peace of mind that your ownership rights are secure, 
                  backed by comprehensive title research and financial protection.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Title Insurance Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            The Title Insurance Process in Middlesex County
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Here's how we protect your property investment from start to finish:
          </Typography>
          
          {titleProcess.map((step, index) => (
            <Card key={index} sx={{ mb: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: '#1e3a8a',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        mx: 'auto',
                      }}
                    >
                      {step.step}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {step.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.details}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Chip
                      label={step.timeframe}
                      color="primary"
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* What Title Insurance Covers */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            What Does Title Insurance Cover?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Title insurance protects against various title defects that could affect your ownership. 
            Here are real examples of coverage:
          </Typography>

          <Grid container spacing={4}>
            {coverageExamples.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <WarningIcon sx={{ color: '#f59e0b', mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {item.issue}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {item.description}
                    </Typography>
                    <Box sx={{ backgroundColor: '#f0fdf4', p: 2, borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#16a34a', mb: 1 }}>
                        Coverage: {item.coverage}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Example:</strong> {item.example}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Types of Policies */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Types of Title Insurance Policies
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%', backgroundColor: '#f0f9ff' }}>
                <HomeIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1e3a8a' }}>
                  Owner's Policy
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Protects you, the homeowner, for as long as you or your heirs own the property.
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="One-time premium at closing" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Coverage equals purchase price" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Protects your equity" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Transferable to heirs" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%', backgroundColor: '#f0fdf4' }}>
                <MoneyIcon sx={{ fontSize: 48, color: '#16a34a', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#16a34a' }}>
                  Lender's Policy
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Protects your lender's investment in the property, typically required for all mortgages.
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Required by most lenders" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Coverage equals loan amount" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Decreases as loan is paid down" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary="Ends when loan is paid off" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Local Considerations */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Special Considerations for Middlesex County
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
                Middlesex County, including Woodbridge, Edison, Perth Amboy, and New Brunswick, 
                has unique characteristics that make title insurance particularly important:
              </Typography>
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Historical Properties
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Many properties in Woodbridge and surrounding areas have ownership histories 
                    dating back to colonial times. This extensive history increases the potential 
                    for title issues from missing documents, unclear transfers, or forgotten liens.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Industrial Legacy
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Middlesex County's industrial history means some properties may have environmental 
                    concerns or easements related to former industrial use. Title insurance helps 
                    identify and protect against these issues.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Transportation Corridors
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Properties near major transportation routes (Garden State Parkway, Route 1, NJ Transit) 
                    may have easements or restrictions that could affect future development or use.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} md={4}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Local Tip
                </Typography>
                <Typography variant="body2">
                  Woodbridge Township has 10 distinct sections, each with its own history. 
                  Our local expertise helps navigate the unique aspects of each area.
                </Typography>
              </Alert>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Why Choose MAD Title?
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Local Expertise" 
                      secondary="Based in Woodbridge since our founding"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Fast Processing" 
                      secondary="Average 2-3 day title search completion"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Comprehensive Coverage" 
                      secondary="We handle complex title issues expertly"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="Title Insurance Guide for Middlesex County"
          resourceType="consumer"
          formType="quote"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Ready to Protect Your Investment?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Get a title insurance quote for your Middlesex County property purchase.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Get Your Quote
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources/closing-cost-calculator"
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Calculate Closing Costs
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TitleInsuranceGuide; 